import {
    Badge,
    Button,
    Checkbox,
    Dialog, Heading, Pane, SelectField, TextInputField,
    Textarea,
    toaster
} from 'evergreen-ui';
import { Formik } from 'formik';
import moment from 'moment';
import { Component } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { connect } from 'react-redux';

import {
    bulkCreateSchedules,
    createSchedule,
    createSwapRequest,
    deleteSchedule,
    getChangeRequests,
    getSchedules,
    getShopEmployees,
    getShops,
    getVacationRequests,
    getWeeklyStats,
    updateSchedule
} from '../actions/ScheduleActions';
import Configuration from '../Config';
import './ScheduleCalendarView.css';

// Set moment locale to start weeks on Monday
moment.updateLocale('en', {
    week: { dow: 1 }
});

const localizer = momentLocalizer(moment);

const DAYS_OF_WEEK = [
    { value: 1, label: 'Luni' },
    { value: 2, label: 'Marți' },
    { value: 3, label: 'Miercuri' },
    { value: 4, label: 'Joi' },
    { value: 5, label: 'Vineri' },
    { value: 6, label: 'Sâmbătă' },
    { value: 0, label: 'Duminică' }
];

const validation = Configuration.Validations.schedule;
const defaultSchedule = Configuration.Defaults.createSchedule;

class ScheduleCalendarView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedShopId: null,
            currentDate: new Date(),
            view: 'week',
            showScheduleModal: false,
            showStatsPanel: false,
            selectedSchedule: null,
            selectedSlot: null,
            isCreating: false,
            selectedTab: 'calendar',
            // Swap mode state
            swapMode: false,
            swapFromSchedule: null,
            swapToSchedule: null,
            showSwapDialog: false,
            swapReason: ''
        };
    }

    // Check if current user is Staff (not Manager or Admin)
    isStaffUser = () => {
        const { user } = this.props;
        return user && user.role && user.role.name === 'Staff';
    }

    // Check if user can edit (Manager or Admin)
    canEdit = () => {
        const { user } = this.props;
        return user && user.role && user.role.level >= 2 && user.role.name !== 'Staff';
    }

    componentDidMount() {
        this.props.dispatch(getShops());
    }

    componentDidUpdate(prevProps, prevState) {
        // Auto-select first shop when shops are loaded
        if (!this.state.selectedShopId && this.props.shops.length > 0 && prevProps.shops.length === 0) {
            this.setState({ selectedShopId: this.props.shops[0].id });
        }

        // Load employees when shop changes
        if (this.state.selectedShopId && this.state.selectedShopId !== prevState.selectedShopId) {
            this.props.dispatch(getShopEmployees(this.state.selectedShopId));
            this.loadSchedules();
        }

        // Load schedules when date changes
        if (this.state.currentDate !== prevState.currentDate && this.state.selectedShopId) {
            this.loadSchedules();
        }
    }

    loadSchedules = () => {
        const { currentDate, selectedShopId, view } = this.state;

        let startDate, endDate;

        if (view === 'month') {
            startDate = moment(currentDate).startOf('month').subtract(7, 'days').format('YYYY-MM-DD');
            endDate = moment(currentDate).endOf('month').add(7, 'days').format('YYYY-MM-DD');
        } else {
            startDate = moment(currentDate).startOf('isoWeek').format('YYYY-MM-DD');
            endDate = moment(currentDate).endOf('isoWeek').format('YYYY-MM-DD');
        }

        this.props.dispatch(getSchedules({ startDate, endDate, shopId: selectedShopId }));
        this.props.dispatch(getWeeklyStats(startDate, selectedShopId));
        this.props.dispatch(getChangeRequests({ pendingOnly: true }));
        // Fetch approved vacations for the same period
        this.props.dispatch(getVacationRequests({
            extraFilters: {
                status: 'approved',
                shopId: selectedShopId
            }
        }));
    }

    handleShopChange = (e) => {
        this.setState({ selectedShopId: parseInt(e.target.value) || null });
    }

    handleNavigate = (newDate) => {
        this.setState({ currentDate: newDate });
    }

    handleViewChange = (newView) => {
        this.setState({ view: newView }, this.loadSchedules);
    }

    handleSelectSlot = (slotInfo) => {
        // Staff cannot create new shifts
        if (!this.canEdit()) {
            return;
        }

        if (!this.state.selectedShopId) {
            toaster.warning("Selectați un magazin mai întâi");
            return;
        }

        this.setState({
            showScheduleModal: true,
            isCreating: true,
            selectedSlot: slotInfo,
            selectedSchedule: {
                ...defaultSchedule,
                shopId: this.state.selectedShopId,
                date: moment(slotInfo.start).format('YYYY-MM-DD'),
                startTime: moment(slotInfo.start).format('HH:mm'),
                endTime: moment(slotInfo.end).format('HH:mm')
            }
        });
    }

    handleSelectEvent = (event) => {
        const { swapMode, swapFromSchedule } = this.state;
        const { user } = this.props;

        // Ignore clicks on vacation events
        if (event.isVacation) {
            return;
        }

        // Ignore clicks on cancelled shifts or shifts during vacation in swap mode
        if (swapMode && (event.resource?.status === 'cancelled' || event.isOnVacation)) {
            toaster.warning("Nu poți face schimb cu o tură anulată");
            return;
        }

        // Handle swap mode
        if (swapMode) {
            // First click - select your shift
            if (!swapFromSchedule) {
                // Staff can only select their own shifts
                if (this.isStaffUser()) {
                    if (event.resource?.employee?.userId !== user.id) {
                        toaster.warning("Poți selecta doar turele tale pentru schimb");
                        return;
                    }
                }

                // Check if the shift is cancelled or on vacation
                if (event.resource?.status === 'cancelled' || event.isOnVacation) {
                    toaster.danger("Nu poți selecta o tură anulată pentru schimb");
                    return;
                }

                this.setState({ swapFromSchedule: event.resource });
                toaster.notify("Acum selectează tura cu care vrei să faci schimb");
                return;
            }

            // Second click - select the shift to swap with
            if (event.resource.id === swapFromSchedule.id) {
                toaster.warning("Nu poți face schimb cu aceeași tură");
                return;
            }

            // Check if requester already has a shift on the swap date
            const requesterHasShiftOnSwapDate = this.props.schedules.some(schedule =>
                schedule.employee?.userId === user.id &&
                schedule.date === event.resource.date &&
                schedule.id !== swapFromSchedule.id
            );

            if (requesterHasShiftOnSwapDate) {
                toaster.danger("Ai deja o tură programată în această zi");
                return;
            }

            // Check if the other employee already has a shift on the requester's date
            const otherEmployeeHasShiftOnRequesterDate = this.props.schedules.some(schedule =>
                schedule.employee?.userId === event.resource.employee?.userId &&
                schedule.date === swapFromSchedule.date &&
                schedule.id !== event.resource.id
            );

            if (otherEmployeeHasShiftOnRequesterDate) {
                toaster.danger("Colegul are deja o tură programată în ziua ta");
                return;
            }

            // Show confirmation dialog
            this.setState({
                swapToSchedule: event.resource,
                showSwapDialog: true
            });
            return;
        }

        // Normal mode - Staff can only view, not edit
        if (!this.canEdit()) {
            toaster.notify("Folosește butonul 'Solicită Schimb' pentru a face un schimb de ture");
            return;
        }

        // Prevent editing cancelled shifts (especially those cancelled due to vacation)
        if (event.resource?.status === 'cancelled' && event.isOnVacation) {
            toaster.warning("Nu poți edita o tură anulată din cauza concediului. Anulează concediul pentru a restaura turele.");
            return;
        }

        this.setState({
            showScheduleModal: true,
            isCreating: false,
            selectedSchedule: event.resource
        });
    }

    // Check if this is the user's own shift
    isOwnShift = (schedule) => {
        const { user } = this.props;
        return schedule?.employee?.userId === user.id;
    }

    // Get pending swap info for a schedule (if any)
    getPendingSwapForSchedule = (scheduleId) => {
        const { pendingChangeRequests } = this.props;
        return pendingChangeRequests.find(req =>
            req.type === 'swap' &&
            (req.originalScheduleId === scheduleId || req.swapWithScheduleId === scheduleId)
        );
    }

    // Enter swap mode
    handleStartSwapMode = () => {
        this.setState({
            swapMode: true,
            swapFromSchedule: null,
            swapToSchedule: null
        });
        toaster.notify("Selectează tura ta pe care vrei să o schimbi");
    }

    // Cancel swap mode
    handleCancelSwapMode = () => {
        this.setState({
            swapMode: false,
            swapFromSchedule: null,
            swapToSchedule: null
        });
    }

    handleSwapConfirm = async () => {
        const { swapFromSchedule, swapToSchedule, swapReason } = this.state;

        try {
            await this.props.dispatch(createSwapRequest(
                swapFromSchedule.id,
                swapToSchedule.id,
                swapReason || 'Schimb de ture solicitat'
            ));

            toaster.success("Cererea de schimb a fost trimisă. Așteaptă aprobarea.");
            this.setState({
                showSwapDialog: false,
                swapMode: false,
                swapFromSchedule: null,
                swapToSchedule: null,
                swapReason: ''
            });

            // Reload to show pending swap indicator
            this.loadSchedules();
        } catch (err) {
            toaster.danger("Eroare la trimiterea cererii de schimb");
        }
    }

    handleSwapCancel = () => {
        this.setState({
            showSwapDialog: false,
            swapMode: false,
            swapFromSchedule: null,
            swapToSchedule: null,
            swapReason: ''
        });
    }

    handleCloseModal = () => {
        this.setState({
            showScheduleModal: false,
            selectedSchedule: null,
            selectedSlot: null,
            isCreating: false
        });
    }

    handleSaveSchedule = async (values) => {
        const { isCreating, selectedSchedule } = this.state;

        try {
            if (isCreating) {
                // Check if we're adding for the full month
                if (values.addForFullMonth && values.selectedDays && values.selectedDays.length > 0) {
                    await this.createMonthlySchedules(values);
                } else {
                    await this.props.dispatch(createSchedule(values));
                }
            } else {
                const result = await this.props.dispatch(updateSchedule(selectedSchedule.id, values));
                // If a change request was created, notify user
                if (result && result.changeRequestCreated) {
                    // Don't reload schedules since no change was made yet
                    this.handleCloseModal();
                    return;
                }
            }
            this.handleCloseModal();
            this.loadSchedules();
        } catch (err) {
            // Error handled in action
        }
    }

    handleDeleteSchedule = async () => {
        const { selectedSchedule } = this.state;

        if (selectedSchedule && selectedSchedule.id) {
            await this.props.dispatch(deleteSchedule(selectedSchedule.id));
            this.handleCloseModal();
            this.loadSchedules();
        }
    }

    createMonthlySchedules = async (values) => {
        const { date, selectedDays, addForFullMonth, ...scheduleData } = values;
        const baseDate = moment(date);
        const startOfMonth = baseDate.clone().startOf('month');
        const endOfMonth = baseDate.clone().endOf('month');

        const schedulesToCreate = [];
        const currentDate = startOfMonth.clone();

        while (currentDate.isSameOrBefore(endOfMonth)) {
            const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.

            if (selectedDays.includes(dayOfWeek)) {
                schedulesToCreate.push({
                    ...scheduleData,
                    date: currentDate.format('YYYY-MM-DD')
                });
            }

            currentDate.add(1, 'day');
        }

        if (schedulesToCreate.length > 0) {
            await this.props.dispatch(bulkCreateSchedules(schedulesToCreate));
        }
    }

    // Convert schedules to calendar events
    getCalendarEvents = () => {
        const { schedules, vacationRequests } = this.props;
        const events = [];

        // Add regular schedules
        schedules.forEach(schedule => {
            // Parse date - handle both ISO strings and simple date strings
            let dateStr;
            if (typeof schedule.date === 'string') {
                // Extract just the date part (YYYY-MM-DD) in case it's an ISO string
                dateStr = schedule.date.substring(0, 10);
            } else {
                dateStr = moment(schedule.date).format('YYYY-MM-DD');
            }

            // Check if this shift is during an approved vacation
            const isOnVacation = vacationRequests.some(vacation => {
                if (vacation.status !== 'approved') return false;
                if (!schedule.employee?.id || vacation.employeeId !== schedule.employee.id) return false;

                const shiftDate = moment(dateStr);
                const vacStart = moment(vacation.startDate);
                const vacEnd = moment(vacation.endDate);

                return shiftDate.isSameOrAfter(vacStart, 'day') && shiftDate.isSameOrBefore(vacEnd, 'day');
            });

            // Skip cancelled schedules unless they're cancelled due to vacation
            if (schedule.status === 'cancelled' && !isOnVacation) {
                return;
            }

            // Create start and end times on the same calendar day
            const year = parseInt(dateStr.substring(0, 4));
            const month = parseInt(dateStr.substring(5, 7)) - 1; // JS months are 0-indexed
            const day = parseInt(dateStr.substring(8, 10));

            const [startHour, startMin] = schedule.startTime.split(':').map(Number);
            const [endHour, endMin] = schedule.endTime.split(':').map(Number);

            const start = new Date(year, month, day, startHour, startMin);
            let end = new Date(year, month, day, endHour, endMin);

            // If end time is before or equal to start time (overnight shift),
            // cap it at end of day for display purposes to prevent multi-day spanning
            if (end <= start) {
                end = new Date(year, month, day, 23, 59);
            }

            const employeeName = schedule.employee?.user?.name || `Employee #${schedule.employeeId}`;
            const timeRange = `${schedule.startTime}-${schedule.endTime}`;
            const pendingSwap = this.getPendingSwapForSchedule(schedule.id);
            const swapIndicator = pendingSwap ? ' ⇄' : '';

            const vacationIndicator = isOnVacation ? ' 🏖️' : '';

            events.push({
                id: schedule.id,
                title: `${employeeName} (${timeRange})${swapIndicator}${vacationIndicator}`,
                start,
                end,
                allDay: false,
                resource: schedule,
                hasPendingSwap: !!pendingSwap,
                pendingSwap,
                isOnVacation
            });
        });

        return events;
    }

    eventStyleGetter = (event) => {
        // Handle vacation events - gray styling
        if (event.isVacation) {
            return {
                style: {
                    background: '#9CA3AF',
                    borderRadius: '4px',
                    opacity: 0.85,
                    color: 'white',
                    border: '1px solid #6B7280',
                    boxShadow: 'none',
                    display: 'block',
                    cursor: 'default',
                    fontStyle: 'italic'
                }
            };
        }

        // Handle shifts during vacation - grayed out
        if (event.isOnVacation) {
            const color = event.resource?.color || event.resource?.employee?.color || '#9CA3AF';
            return {
                style: {
                    background: '#9CA3AF',
                    borderRadius: '4px',
                    opacity: 0.5,
                    color: 'white',
                    border: '1px solid #6B7280',
                    boxShadow: 'none',
                    display: 'block',
                    cursor: 'default',
                    fontStyle: 'italic',
                    textDecoration: 'line-through'
                }
            };
        }

        const color = event.resource?.color || event.resource?.employee?.color || '#4CAF50';
        const { swapMode, swapFromSchedule } = this.state;
        const isSelectedForSwap = swapFromSchedule?.id === event.resource?.id;
        const isOwnShift = this.isOwnShift(event.resource);
        const hasPendingSwap = event.hasPendingSwap;

        // In swap mode: highlight own shifts differently if Staff, or highlight selected shift
        let border = '0';
        let boxShadow = 'none';
        let opacity = 0.9;
        let background = color;

        // Show pending swap with striped pattern
        if (hasPendingSwap && !swapMode) {
            background = `repeating-linear-gradient(
                45deg,
                ${color},
                ${color} 10px,
                ${color}dd 10px,
                ${color}dd 20px
            )`;
            border = '2px solid #FF9800';
        }

        if (swapMode) {
            if (isSelectedForSwap) {
                border = '3px solid #FFD700';
                boxShadow = '0 0 10px rgba(255, 215, 0, 0.8)';
            } else if (this.isStaffUser() && !swapFromSchedule && isOwnShift) {
                // Highlight own shifts when Staff needs to select their shift first
                border = '2px dashed #2196F3';
            } else if (swapFromSchedule && !isSelectedForSwap) {
                // Dim own shift once selected, highlight others as potential swap targets
                border = '2px dashed #4CAF50';
            }
        }

        return {
            style: {
                background,
                borderRadius: '4px',
                opacity,
                color: 'white',
                border,
                boxShadow,
                display: 'block',
                cursor: swapMode ? 'pointer' : 'default',
                transition: 'all 0.15s ease-in-out'
            }
        };
    }

    renderStatsPanel = () => {
        const { weeklyStats } = this.props;

        return (
            <Pane
                width={300}
                padding={16}
                background="tint1"
                borderLeft="muted"
                overflowY="auto"
            >
                <Heading size={500} marginBottom={16}>Statistici Săptămânale</Heading>

                {weeklyStats.length === 0 ? (
                    <Pane>Nu există date pentru această perioadă</Pane>
                ) : (
                    weeklyStats.map(stat => (
                        <Pane
                            key={stat.employeeId}
                            padding={12}
                            marginBottom={8}
                            background="white"
                            borderRadius={4}
                            elevation={1}
                        >
                            <Pane display="flex" justifyContent="space-between" alignItems="center">
                                <strong>{stat.employeeName}</strong>
                                <Badge
                                    color={stat.hoursCompliance === 'ok' ? 'green' : stat.hoursCompliance === 'warning' ? 'yellow' : 'red'}
                                >
                                    {stat.totalHours.toFixed(1)}h / {stat.contractHours}h
                                </Badge>
                            </Pane>
                            <Pane marginTop={8} fontSize={12}>
                                <Pane display="flex" justifyContent="space-between">
                                    <span>Zile lucrate:</span>
                                    <span>{stat.workDays}</span>
                                </Pane>
                                <Pane display="flex" justifyContent="space-between" marginTop={4}>
                                    <span>2 zile libere consecutive:</span>
                                    <Badge color={stat.daysOffCompliance === 'ok' ? 'green' : 'red'}>
                                        {stat.hasConsecutiveDaysOff ? 'Da' : 'Nu'}
                                    </Badge>
                                </Pane>
                            </Pane>
                        </Pane>
                    ))
                )}
            </Pane>
        );
    }

    render() {
        const { shops, employees } = this.props;
        const {
            selectedShopId, currentDate, view, showScheduleModal,
            selectedSchedule, isCreating, showStatsPanel,
            swapMode, swapFromSchedule
        } = this.state;

        const events = this.getCalendarEvents();
        const isStaff = !this.canEdit();

        return (
            <Pane display="flex" flexDirection="column" height="calc(100vh - 120px)">
                {/* Header */}
                <Pane
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    padding={16}
                    background="tint1"
                    borderBottom="muted"
                >
                    <Pane display="flex" alignItems="center" gap={16}>
                        <Heading size={600}>Program Angajați</Heading>

                        <SelectField
                            label=""
                            value={selectedShopId || ''}
                            onChange={this.handleShopChange}
                            width={200}
                            marginBottom={0}
                        >
                            <option value="">Selectează magazin...</option>
                            {shops.map(shop => (
                                <option key={shop.id} value={shop.id}>
                                    {shop.name}
                                </option>
                            ))}
                        </SelectField>

                        {isStaff && selectedShopId && !swapMode && (
                            <Button
                                appearance="primary"
                                intent="none"
                                iconBefore="swap-horizontal"
                                onClick={this.handleStartSwapMode}
                            >
                                Solicită Schimb
                            </Button>
                        )}

                        {swapMode && (
                            <Pane display="flex" alignItems="center" gap={8}>
                                <Badge color="orange" isSolid>
                                    {!swapFromSchedule
                                        ? '➀ Selectează tura ta'
                                        : '➁ Selectează tura pentru schimb'
                                    }
                                </Badge>
                                {swapFromSchedule && (
                                    <Badge color="blue">
                                        {swapFromSchedule.employee?.user?.name} - {swapFromSchedule.date}
                                    </Badge>
                                )}
                                <Button
                                    intent="danger"
                                    onClick={this.handleCancelSwapMode}
                                >
                                    Anulează
                                </Button>
                            </Pane>
                        )}
                    </Pane>

                    <Pane display="flex" gap={8} alignItems="center">
                        {/* Legend for pending swaps */}
                        {this.props.pendingChangeRequests.length > 0 && !swapMode && (
                            <Pane display="flex" alignItems="center" gap={4}>
                                <Pane
                                    width={16}
                                    height={16}
                                    borderRadius={2}
                                    border="2px solid #FF9800"
                                    background="repeating-linear-gradient(45deg, #4CAF50, #4CAF50 3px, #4CAF50dd 3px, #4CAF50dd 6px)"
                                />
                                <span style={{ fontSize: 12, color: '#666' }}>Schimb în așteptare</span>
                            </Pane>
                        )}

                        <Button
                            iconBefore={showStatsPanel ? "chevron-right" : "chart"}
                            onClick={() => this.setState({ showStatsPanel: !showStatsPanel })}
                        >
                            {showStatsPanel ? 'Ascunde Statistici' : 'Statistici'}
                        </Button>
                    </Pane>
                </Pane>

                {/* Main Content */}
                <Pane display="flex" flex={1} overflow="auto">
                    {/* Calendar */}
                    <Pane flex={1} padding={16} className="schedule-calendar-view">
                        {!selectedShopId ? (
                            <Pane
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                height="100%"
                            >
                                <Heading size={400} color="muted">
                                    Selectați un magazin pentru a vizualiza programul
                                </Heading>
                            </Pane>
                        ) : (
                            <Calendar
                                localizer={localizer}
                                events={events}
                                startAccessor="start"
                                endAccessor="end"
                                date={currentDate}
                                view={view}
                                onNavigate={this.handleNavigate}
                                onView={this.handleViewChange}
                                onSelectSlot={this.handleSelectSlot}
                                onSelectEvent={this.handleSelectEvent}
                                selectable={this.canEdit() || swapMode}
                                eventPropGetter={this.eventStyleGetter}
                                views={['month', 'week', 'day']}
                                step={30}
                                timeslots={2}
                                min={new Date(2020, 0, 1, 6, 0)}
                                max={new Date(2020, 0, 1, 23, 0)}
                                dayLayoutAlgorithm="no-overlap"
                                style={{ height: view === 'month' ? 'auto' : '800px', minHeight: '700px' }}
                                messages={{
                                    today: 'Azi',
                                    previous: 'Înapoi',
                                    next: 'Înainte',
                                    month: 'Lună',
                                    week: 'Săptămână',
                                    day: 'Zi',
                                    agenda: 'Agendă'
                                }}
                            />
                        )}
                    </Pane>

                    {/* Stats Panel */}
                    {showStatsPanel && this.renderStatsPanel()}
                </Pane>

                {/* Swap Request Dialog */}
                <Dialog
                    isShown={this.state.showSwapDialog}
                    title="Solicită Schimb de Ture"
                    onCloseComplete={this.handleSwapCancel}
                    confirmLabel="Trimite Cererea"
                    onConfirm={this.handleSwapConfirm}
                    cancelLabel="Anulează"
                >
                    {this.state.swapFromSchedule && this.state.swapToSchedule && (
                        <Pane>
                            <Pane background="tint1" padding={16} borderRadius={4} marginBottom={16}>
                                <Heading size={400} marginBottom={8}>Schimb propus:</Heading>
                                <Pane display="flex" alignItems="center" justifyContent="space-between">
                                    <Pane textAlign="center" flex={1}>
                                        <Badge color="blue" marginBottom={8}>Tura ta</Badge>
                                        <Pane><strong>{this.state.swapFromSchedule.employee?.user?.name}</strong></Pane>
                                        <Pane>{this.state.swapFromSchedule.date}</Pane>
                                        <Pane>{this.state.swapFromSchedule.startTime} - {this.state.swapFromSchedule.endTime}</Pane>
                                    </Pane>
                                    <Pane padding={16}>↔️</Pane>
                                    <Pane textAlign="center" flex={1}>
                                        <Badge color="orange" marginBottom={8}>Tura colegului</Badge>
                                        <Pane><strong>{this.state.swapToSchedule.employee?.user?.name}</strong></Pane>
                                        <Pane>{this.state.swapToSchedule.date}</Pane>
                                        <Pane>{this.state.swapToSchedule.startTime} - {this.state.swapToSchedule.endTime}</Pane>
                                    </Pane>
                                </Pane>
                            </Pane>
                            <TextInputField
                                label="Motiv (opțional)"
                                placeholder="De ce doriți să schimbați turele?"
                                value={this.state.swapReason}
                                onChange={(e) => this.setState({ swapReason: e.target.value })}
                            />
                        </Pane>
                    )}
                </Dialog>

                {/* Schedule Modal */}
                <Dialog
                    isShown={showScheduleModal}
                    title={isCreating ? 'Adaugă Tură' : 'Editează Tură'}
                    onCloseComplete={this.handleCloseModal}
                    hasFooter={false}
                    width={500}
                    shouldCloseOnOverlayClick={false}
                >
                    <Pane maxHeight="70vh" overflowY="auto">
                    {selectedSchedule && (
                        <Formik
                            initialValues={{
                                shopId: selectedSchedule.shopId || selectedShopId,
                                employeeId: selectedSchedule.employeeId || '',
                                date: selectedSchedule.date || '',
                                startTime: selectedSchedule.startTime || '09:00',
                                endTime: selectedSchedule.endTime || '17:00',
                                notes: selectedSchedule.notes || '',
                                addForFullMonth: false,
                                selectedDays: [],
                                changeReason: ''
                            }}
                            validationSchema={validation}
                            onSubmit={this.handleSaveSchedule}
                        >
                            {({ values, errors, handleChange, handleSubmit, setFieldValue }) => (
                                <form onSubmit={handleSubmit}>
                                    <SelectField
                                        label="Angajat"
                                        name="employeeId"
                                        required
                                        value={values.employeeId}
                                        onChange={(e) => setFieldValue('employeeId', parseInt(e.target.value))}
                                        validationMessage={errors.employeeId}
                                    >
                                        <option value="">Selectează angajat...</option>
                                        {employees.map(emp => (
                                            <option key={emp.id} value={emp.id}>
                                                {emp.user?.name || `Employee #${emp.id}`}
                                            </option>
                                        ))}
                                    </SelectField>

                                    <TextInputField
                                        label="Data"
                                        name="date"
                                        type="date"
                                        required
                                        value={values.date}
                                        onChange={handleChange}
                                        validationMessage={errors.date}
                                        hint={isCreating && values.addForFullMonth ? "Luna pentru care se adaugă turele" : null}
                                    />

                                    {/* Add for full month option - only show when creating */}
                                    {isCreating && (
                                        <Pane
                                            marginBottom={16}
                                            padding={12}
                                            background="tint1"
                                            borderRadius={4}
                                        >
                                            <Checkbox
                                                label="Adaugă tură pentru toată luna"
                                                checked={values.addForFullMonth}
                                                onChange={(e) => {
                                                    setFieldValue('addForFullMonth', e.target.checked);
                                                    if (!e.target.checked) {
                                                        setFieldValue('selectedDays', []);
                                                    }
                                                }}
                                            />

                                            {values.addForFullMonth && (
                                                <Pane marginTop={12}>
                                                    <Pane marginBottom={8} fontSize={14} fontWeight={500}>
                                                        Selectează zilele săptămânii:
                                                    </Pane>
                                                    <Pane display="flex" flexWrap="wrap" gap={8}>
                                                        {DAYS_OF_WEEK.map(day => (
                                                            <Checkbox
                                                                key={day.value}
                                                                label={day.label}
                                                                checked={values.selectedDays.includes(day.value)}
                                                                onChange={(e) => {
                                                                    const newDays = e.target.checked
                                                                        ? [...values.selectedDays, day.value]
                                                                        : values.selectedDays.filter(d => d !== day.value);
                                                                    setFieldValue('selectedDays', newDays);
                                                                }}
                                                            />
                                                        ))}
                                                    </Pane>
                                                    {values.selectedDays.length > 0 && values.date && (
                                                        <Pane marginTop={8} fontSize={12} color="muted">
                                                            Se vor crea ture pentru toate zilele selectate din luna {moment(values.date).format('MMMM YYYY')}
                                                        </Pane>
                                                    )}
                                                </Pane>
                                            )}
                                        </Pane>
                                    )}

                                    <Pane display="flex" gap={16}>
                                        <Pane flex={1}>
                                            <TextInputField
                                                label="Ora Început"
                                                name="startTime"
                                                type="time"
                                                required
                                                value={values.startTime}
                                                onChange={handleChange}
                                                validationMessage={errors.startTime}
                                            />
                                        </Pane>
                                        <Pane flex={1}>
                                            <TextInputField
                                                label="Ora Sfârșit"
                                                name="endTime"
                                                type="time"
                                                required
                                                value={values.endTime}
                                                onChange={handleChange}
                                                validationMessage={errors.endTime}
                                            />
                                        </Pane>
                                    </Pane>

                                    <Pane marginBottom={16}>
                                        <label>Note</label>
                                        <Textarea
                                            name="notes"
                                            value={values.notes}
                                            onChange={handleChange}
                                            placeholder="Note sau observații..."
                                        />
                                    </Pane>

                                    {/* Change reason field - only show when editing */}
                                    {!isCreating && this.isStaffUser() && (
                                        <Pane
                                            marginBottom={16}
                                            padding={12}
                                            background="yellowTint"
                                            borderRadius={4}
                                        >
                                            <Pane marginBottom={8} fontSize={14} fontWeight={500} color="warning">
                                                ⚠️ Modificările necesită aprobare
                                            </Pane>
                                            <label>Motivul modificării</label>
                                            <Textarea
                                                name="changeReason"
                                                value={values.changeReason}
                                                onChange={handleChange}
                                                placeholder="Explicați motivul pentru care doriți să modificați această tură..."
                                            />
                                        </Pane>
                                    )}

                                    <Pane display="flex" justifyContent="space-between" marginTop={16}>
                                        {!isCreating && (
                                            <Button
                                                intent="danger"
                                                onClick={this.handleDeleteSchedule}
                                            >
                                                Șterge
                                            </Button>
                                        )}
                                        <Pane display="flex" gap={8} marginLeft="auto">
                                            <Button onClick={this.handleCloseModal}>
                                                Anulează
                                            </Button>
                                            <Button type="submit" appearance="primary">
                                                {isCreating ? 'Adaugă' : 'Salvează'}
                                            </Button>
                                        </Pane>
                                    </Pane>
                                </form>
                            )}
                        </Formik>
                    )}
                    </Pane>
                </Dialog>
            </Pane>
        );
    }
}

function mapStateToProps(state) {
    return {
        schedules: state.scheduleProgram.schedules,
        shops: state.scheduleProgram.shops,
        employees: state.scheduleProgram.employees,
        weeklyStats: state.scheduleProgram.weeklyStats,
        pendingChangeRequests: state.scheduleProgram.pendingChangeRequests || [],
        vacationRequests: state.scheduleProgram.vacationRequests || [],
        loading: state.scheduleProgram.loading,
        user: state.authentication.user
    };
}

export default connect(mapStateToProps)(ScheduleCalendarView);
